import React, { useMemo, useState } from 'react';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import type { TripResponseArgs } from '@this/domain/trip/trip2';
import type { ChatMessage } from '../types/messages';
import type { UserInfo } from '../types/user';
import type { ProposalNotification } from '../hooks/useAiMessages';
import { useChatInput } from '../hooks/useChatInput';
import { useConversationState } from '../hooks/useConversationState';
import type { ProposalBundle } from '../api/proposalBundles';
import type { ReservationInfo } from '../api/reservationInfo';
import type { FormValues } from './message_section/ReservationInfoFormTypes';
import SidebarMenu from './SidebarMenu';
import ChatArea from './ChatArea';
import InfoPanel from './InfoPanel';
import RulesPanel from './RulesPanel';
import SessionWarningModal from './SessionWarningModal';
import HistoryListPanel from './HistoryListPanel';
import UserChatRuleSettingsModal from './UserChatRuleSettingsModal';

interface Props {
  tripId: string;
  trip: TripResponseArgs | null;
  user?: UserInfo;
  pusherKey: string;
  pusherChannelPrefix: string;
  headerTitle?: string;
  subtitle?: string;
  children?: React.ReactNode;
  enableMessaging?: boolean;
  initialMessages?: ChatMessage[];
  onProposalReceived?: (notification: ProposalNotification) => void;
  onReservationFormReceived: () => void;
  proposalBundles?: ProposalBundle[];
  onSelectItem?: (bundleId: number, itemId: number) => void;
  onCloseItem?: (bundleId: number) => void;
  reservationInfo: ReservationInfo | null;
  onSubmitReservationInfo?: (values: FormValues) => Promise<boolean>;
  isSubmittingReservationInfo?: boolean;
  onArrangementRequestSuccess?: () => Promise<void>;
  onReservationInfoFormEditableChange: (editable: boolean) => void;
  isReservationInfoFormEditable: boolean;
  onTripUpdated?: () => void;
}

const TripAiMessagesContainer = ({
  tripId,
  trip,
  user,
  pusherKey,
  pusherChannelPrefix,
  headerTitle,
  subtitle,
  children,
  enableMessaging,
  initialMessages = [],
  onProposalReceived,
  onReservationFormReceived,
  proposalBundles = [],
  onSelectItem,
  onCloseItem,
  reservationInfo,
  onSubmitReservationInfo,
  isSubmittingReservationInfo,
  onArrangementRequestSuccess,
  onReservationInfoFormEditableChange,
  isReservationInfoFormEditable,
  onTripUpdated
}: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const isTripLoading = trip === null;
  const [showHistoryList, setShowHistoryList] = useState(false);
  const [showUserChatRuleSettings, setShowUserChatRuleSettings] = useState(false);

  const chatInput = useChatInput();
  const conversationState = useConversationState(tripId);
  const [isArrangementRequested, setIsArrangementRequested] = useState(false);

  const handleShowHistoryList = () => {
    setShowHistoryList(true);
  };

  const handleHideHistoryList = () => {
    setShowHistoryList(false);
  };

  return (
    <div style={styles.root}>
      <div style={styles.body}>
        <SidebarMenu
          currentTripId={tripId}
          onShowAllHistory={handleShowHistoryList}
          onOpenUserChatRuleSettings={() => setShowUserChatRuleSettings(true)}
        />
        {showHistoryList ? (
          <HistoryListPanel onBack={handleHideHistoryList} />
        ) : (
          <>
            <ChatArea
              tripId={tripId}
              trip={trip}
              user={user}
              pusherKey={pusherKey}
              pusherChannelPrefix={pusherChannelPrefix}
              headerTitle={headerTitle}
              subtitle={subtitle}
              enableMessaging={!isTripLoading && enableMessaging !== false}
              initialMessages={initialMessages}
              onProposalReceived={onProposalReceived}
              onReservationFormReceived={onReservationFormReceived}
              proposalBundles={proposalBundles}
              onSelectItem={onSelectItem}
              onCloseItem={onCloseItem}
              reservationInfo={reservationInfo}
              onSubmitReservationInfo={onSubmitReservationInfo}
              isSubmittingReservationInfo={isSubmittingReservationInfo}
              onArrangementRequestSuccess={onArrangementRequestSuccess}
              onReservationInfoFormEditableChange={onReservationInfoFormEditableChange}
              inputValue={chatInput.inputValue}
              onInputChange={chatInput.handleInputChange}
              appendToInput={chatInput.appendToInput}
              resetInput={chatInput.resetInput}
              textareaRef={chatInput.textareaRef}
              blockers={conversationState.blockers}
              searchTargets={conversationState.searchTargets}
              onConversationStateChanged={conversationState.refetch}
              onArrangementRequestedChange={setIsArrangementRequested}
            >
              {children}
            </ChatArea>
            {trip ? (
              <InfoPanel
                tripId={tripId}
                trip={trip}
                proposalBundles={proposalBundles}
                isEditDisabled={!isReservationInfoFormEditable}
                reservationInfo={reservationInfo}
                onTripUpdated={onTripUpdated}
                inputSupport={{
                  blockers: conversationState.blockers,
                  decidedFields: conversationState.decidedFields,
                  searchTargets: conversationState.searchTargets,
                  readyToSearch: conversationState.readyToSearch,
                  isLoading: conversationState.isLoading,
                  isArrangementRequested,
                  hasConversationState: conversationState.hasConversationState,
                  onChipClick: chatInput.appendToInput,
                  onBulkInsert: chatInput.appendToInput
                }}
              />
            ) : (
              <PlaceholderPanel />
            )}
          </>
        )}
        <RulesPanel />
        <SessionWarningModal />
        <UserChatRuleSettingsModal
          open={showUserChatRuleSettings}
          onClose={() => setShowUserChatRuleSettings(false)}
        />
      </div>
    </div>
  );
};

const getStyles = (theme: AITTheme) => ({
  root: {
    width: '100%',
    height: 'calc(100vh - 80px)',
    display: 'flex' as const,
    flexDirection: 'column' as const,
    minHeight: 0
  },
  body: {
    display: 'flex' as const,
    flex: 1,
    position: 'relative' as const,
    backgroundColor: theme.whiteColor,
    border: `1px solid ${theme.grayBorderColor}`,
    borderRadius: '8px',
    overflow: 'hidden',
    minHeight: 0
  }
});

const PlaceholderPanel = () => {
  const { theme } = useAITTheme();
  const styles = useMemo(
    () => ({
      width: '320px',
      padding: '20px',
      backgroundColor: theme.contentBgColor,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }),
    [theme]
  );
  return (
    <div style={styles}>
      <Loading />
    </div>
  );
};

export default TripAiMessagesContainer;
